import { useState } from "react";
import { useDebounce } from "use-debounce";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api/api";
import {
  Search,
  Columns2,
  ArrowUpDown,
  SlidersHorizontal,
  Table2,
  List,
  MoreHorizontal,
  ChevronDown,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ChevronsUpDown,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import type { Recipe, RecipeStorageContainer, RecipeTool, StorageContainer, Tool } from "@/types/recipe";
import {
  useRecipeStorageContainers,
  useCreateStorageContainerCatalog,
  useLinkStorageContainer,
  useUpdateRecipeStorageContainer,
  useUnlinkStorageContainer,
  useRecipeTools,
  useCreateToolCatalog,
  useUpdateStorageContainerCatalog,
  useLinkTool,
  useUpdateRecipeTool,
  useUnlinkTool,
} from "@/hooks/recipes";
import { recipeRequests } from "@/lib/api/requests/recipes";
import { useContainerFlags } from "@/hooks/useContainerFlags";
import { flagColorClass, flagMeaning, flagOptions } from "@/lib/container-flags";
import type { FlagCode } from "@/types/container-flag";
import {
  containerSubTypeOptions,
  toolTypeOptions,
  useContainerSubTypes,
  TOOL_TYPES,
  useContainerSubTypeNames,
  useToolTypeNames,
} from "./container-types";
import { useTenantLists, useUpdateTenantList } from "@/hooks/useTenantLists";
import { VirtualScroller } from "@/components/common/VirtualScroller";
import { VirtualSelectField } from "@/components/common/VirtualSelectField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface StorageContainersTabProps {
  recipe: Recipe;
  canManage?: boolean;
}

interface JunctionFields {
  qty: number;
  required: boolean;
  flag: string;
  notes: string;
}

const EMPTY_JUNCTION: JunctionFields = { qty: 1, required: true, flag: "", notes: "" };

const ROWS_PER_PAGE = 10;

type SubTab = "containers" | "tools";

/**
 * A flag, shown as what it means.
 *
 * The dot alone was decoration — nobody outside the kitchen that chose it knows
 * what red stands for, and a colour nobody can read looks like information
 * without being any (T-1005). So the meaning is written beside it, and is what a
 * screen reader is given.
 */
function FlagDot({ flag, codes }: { flag?: string | null; codes?: readonly FlagCode[] }) {
  const meaning = flagMeaning(codes, flag);
  if (!meaning) return <span className="text-(--color-text-secondary)">—</span>;
  return (
    <span className="flex items-center gap-2">
      <span
        className={`inline-block size-6 shrink-0 rounded-full ${flagColorClass(flag!)}`}
        aria-hidden="true"
      />
      <span className="truncate text-[13px] text-(--color-text-secondary)">{meaning}</span>
    </span>
  );
}

function JunctionFormFields({
  form,
  onChange,
  codes,
}: {
  form: JunctionFields;
  onChange: (f: JunctionFields) => void;
  codes?: readonly FlagCode[];
}) {
  const { t } = useLingui();
  const options = flagOptions(codes, form.flag);
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>{t`Quantity`}</Label>
          <Input
            type="number" min={1} value={form.qty}
            onChange={(e) => onChange({ ...form, qty: Math.max(1, parseInt(e.target.value) || 1) })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t`Flag`}</Label>
          {options.length === 0 ? (
            <p className="pt-1 text-xs text-(--color-text-secondary)">
              {t`No flag colours are set up yet. An administrator defines what each colour means under Settings.`}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {options.map((code) => (
                <Button key={code.color} type="button" variant="ghost" size="icon"
                  onClick={() => onChange({ ...form, flag: form.flag === code.color ? "" : code.color })}
                  className={`size-7 rounded-full p-0 ring-offset-2 hover:bg-transparent ${flagColorClass(code.color)} ${form.flag === code.color ? "ring-2 ring-foreground opacity-100" : "opacity-60 hover:opacity-100"}`}
                  title={code.meaning}
                  aria-label={code.meaning}
                  aria-pressed={form.flag === code.color}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>{t`Notes`}</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => onChange({ ...form, notes: e.target.value })}
          placeholder={t`Usage notes...`}
          className="resize-none" rows={2}
        />
      </div>
      <div className="flex items-center gap-3">
        <Switch
          id="junction-required"
          checked={form.required}
          onCheckedChange={(v) => onChange({ ...form, required: v })}
        />
        <Label htmlFor="junction-required">{t`Required`}</Label>
      </div>
    </>
  );
}

// ─── Containers sub-tab ────────────────────────────────────────────────────────

function ContainersPanel({ recipeId, canManage }: { recipeId: number; canManage: boolean }) {
  const { t } = useLingui();
  const { data, isLoading, isError } = useRecipeStorageContainers(recipeId);
  const { data: flags } = useContainerFlags();
  const codes = flags?.codes;
  const subTypeNames = useContainerSubTypeNames();
  const subTypes = useContainerSubTypes();
  const createCatalogMutation = useCreateStorageContainerCatalog();
  const updateCatalogMutation = useUpdateStorageContainerCatalog();
  const linkMutation = useLinkStorageContainer();
  const updateMutation = useUpdateRecipeStorageContainer();
  const unlinkMutation = useUnlinkStorageContainer();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Link dialog
  const [linkOpen, setLinkOpen] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState<StorageContainer | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSubType, setNewSubType] = useState("");
  const [newSizeOpen, setNewSizeOpen] = useState("");
  const [junction, setJunction] = useState<JunctionFields>(EMPTY_JUNCTION);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<RecipeStorageContainer | null>(null);
  const [editJunction, setEditJunction] = useState<JunctionFields>(EMPTY_JUNCTION);

  const allRows = data?.data ?? [];
  const filteredRows = search
    ? allRows.filter((r) =>
        r.storageContainer.name.toLowerCase().includes(search.toLowerCase()) ||
        r.storageContainer.subType?.toLowerCase().includes(search.toLowerCase()),
      )
    : allRows;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const pagedRows = filteredRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  function openLink() {
    setSelectedCatalog(null);
    setCreateMode(false);
    setNewName("");
    setNewSubType("");
    setNewSizeOpen("");
    setJunction(EMPTY_JUNCTION);
    setLinkOpen(true);
  }

  function openEdit(row: RecipeStorageContainer) {
    setEditingRow(row);
    setEditJunction({ qty: row.qty, required: row.required, flag: row.flag ?? "", notes: row.notes ?? "" });
    setEditOpen(true);
  }

  async function handleLink() {
    try {
      let storageContainerId: number;
      if (createMode) {
        const created = await createCatalogMutation.mutateAsync({
          name: newName,
          subType: newSubType || undefined,
          sizeOpen: newSizeOpen || undefined,
        });
        storageContainerId = created.id;
      } else {
        if (!selectedCatalog) return;
        const nextSubType = newSubType.trim() || undefined;
        const nextSizeOpen = newSizeOpen.trim() || undefined;
        const catalogChanged =
          nextSubType !== (selectedCatalog.subType ?? undefined) ||
          nextSizeOpen !== (selectedCatalog.sizeOpen ?? undefined);

        if (catalogChanged) {
          await updateCatalogMutation.mutateAsync({
            id: selectedCatalog.id,
            data: { subType: nextSubType ?? null, sizeOpen: nextSizeOpen ?? null },
          });
        }
        storageContainerId = selectedCatalog.id;
      }
      await linkMutation.mutateAsync({
        recipeId,
        data: {
          storageContainerId,
          qty: junction.qty,
          required: junction.required,
          flag: junction.flag || undefined,
          notes: junction.notes || undefined,
        },
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save storage container`));
      return;
    }
    toast.success(t`Storage container added`);
    setLinkOpen(false);
  }

  async function handleUpdate() {
    if (!editingRow) return;
    try {
      await updateMutation.mutateAsync({
        recipeId,
        id: editingRow.id,
        data: {
          qty: editJunction.qty,
          required: editJunction.required,
          flag: editJunction.flag || undefined,
          notes: editJunction.notes || undefined,
        },
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save storage container`));
      return;
    }
    toast.success(t`Storage container updated`);
    setEditOpen(false);
  }

  const isLinking =
    createCatalogMutation.isPending || updateCatalogMutation.isPending || linkMutation.isPending;
  const linkDisabled = createMode ? !newName.trim() || isLinking : !selectedCatalog || isLinking;

  return (
    <>
      <TablePanel
        search={search} onSearch={(v) => { setSearch(v); setPage(1); }}
        canManage={canManage} onAdd={openLink} addLabel={t`Add container`}
        isLoading={isLoading} isError={isError}
        headers={[t`Item Name`, t`Sub-type`, t`Size`, t`Qty`, t`Required`, t`Flag`, t`Notes`]}
        rows={pagedRows} canManageRow={canManage}
        renderRow={(row) => (
          <TableRow key={row.id} className="h-17">
            <TableCell className="font-semibold text-[14px]">{row.storageContainer.name}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.storageContainer.subType ?? <span className="text-(--color-text-secondary)">—</span>}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.storageContainer.sizeOpen ?? <span className="text-(--color-text-secondary)">—</span>}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.qty}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.required ? t`Yes` : <span className="text-(--color-text-secondary)">{t`Optional`}</span>}</TableCell>
            <TableCell><FlagDot flag={row.flag} codes={codes} /></TableCell>
            <TableCell className="text-[14px] font-medium text-(--color-text-secondary)">{row.notes ?? "—"}</TableCell>
            {canManage && (
              <TableCell>
                <RowMenu
                  onEdit={() => openEdit(row)}
                  onDelete={() =>
                    unlinkMutation.mutate(
                      { recipeId, id: row.id },
                      {
                        onSuccess: () => toast.success(t`Storage container removed`),
                        onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to remove storage container`)),
                      },
                    )
                  }
                  isDeleting={unlinkMutation.isPending}
                />
              </TableCell>
            )}
          </TableRow>
        )}
        emptyLabel={search ? t`No results match your search.` : t`No storage containers yet.`}
        page={page} totalPages={totalPages} onPageChange={setPage}
        total={filteredRows.length}
      />

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-175">
          <DialogHeader>
            <DialogTitle>{t`Add storage container`}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label>{t`Item name`}</Label>
                {createMode ? (
                  <div className="flex flex-col gap-1.5">
                    <Button
                      variant="ghost"
                      onClick={() => { setCreateMode(false); setNewName(""); }}
                      className="h-auto w-fit gap-1 p-0 text-xs text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
                    >
                      <ChevronLeft className="size-3.5" />{t`Select existing`}
                    </Button>
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t`e.g. 1/3 GN Pan, Deli Cup`}
                      autoFocus
                      className="h-11 rounded-[8px] border-(--color-border)"
                    />
                  </div>
                ) : (
                  <VirtualSelectField<StorageContainer>
                    queryKeyBase={["storage-containers", "catalog-picker"]}
                    buildQueryFn={(search) => (page, pageSize) =>
                      recipeRequests.listStorageContainersCatalog({ page, limit: pageSize, search: search || undefined })
                    }
                    getItemValue={(item) => String(item.id)}
                    getItemLabel={(item) => item.name}
                    value={selectedCatalog ? String(selectedCatalog.id) : ""}
                    displayValue={selectedCatalog?.name}
                    onChange={(_, item) => {
                      setSelectedCatalog(item);
                      setNewSubType(item.subType ?? "");
                      setNewSizeOpen(item.sizeOpen ?? "");
                    }}
                    onClear={() => {
                      setSelectedCatalog(null);
                      setNewSubType("");
                      setNewSizeOpen("");
                    }}
                    placeholder={t`Select`}
                    searchPlaceholder={t`Search containers...`}
                    emptyMessage={t`No containers found.`}
                    footer={
                      <Button
                        variant="ghost"
                        onClick={() => setCreateMode(true)}
                        className="h-auto w-full justify-start gap-2 px-3 py-2.5 text-sm font-medium text-(--color-action) hover:bg-(--color-surface-muted)/50 hover:text-(--color-action)"
                      >
                        <Plus className="size-4" />{t`Create new container`}
                      </Button>
                    }
                  />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label>{t`Sub-type`}</Label>
                <Select
                  value={newSubType || "none"}
                  onValueChange={(v) => setNewSubType(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="h-11 rounded-[8px] border-(--color-border)" aria-label={t`Sub-type`}>
                    <SelectValue placeholder={t`Select`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t`Unspecified`}</SelectItem>
                    {containerSubTypeOptions(newSubType, subTypes).map((subType) => (
                      <SelectItem key={subType} value={subType}>
                        {subTypeNames[subType] ?? subType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label>{t`Size/Spec`}</Label>
                <Input
                  value={newSizeOpen}
                  onChange={(e) => setNewSizeOpen(e.target.value)}
                  placeholder={t`Select`}
                  className="h-11 rounded-[8px] border-(--color-border)"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>{t`Qty`}</Label>
                <Input
                  type="number"
                  min={1}
                  value={junction.qty}
                  onChange={(e) => setJunction({ ...junction, qty: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="h-11 rounded-[8px] border-(--color-border)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label>{t`Required`}</Label>
                <Select
                  value={junction.required ? "yes" : "no"}
                  onValueChange={(v) => setJunction({ ...junction, required: v === "yes" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">{t`Yes`}</SelectItem>
                    <SelectItem value="no">{t`No`}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label>{t`Flag`}</Label>
                <Select
                  value={junction.flag || "none"}
                  onValueChange={(v) => setJunction({ ...junction, flag: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t`Select`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t`None`}</SelectItem>
                    {/* Only colours this kitchen has given a meaning — the list
                        is the colour code, not a paint chart. */}
                    {flagOptions(codes, junction.flag).map((code) => (
                      <SelectItem key={code.color} value={code.color}>
                        <span className="flex items-center gap-2">
                          <span className={`inline-block size-3 rounded-full ${flagColorClass(code.color)}`} />
                          {code.meaning}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label>{t`Used for`}</Label>
              <Textarea
                value={junction.notes}
                onChange={(e) => setJunction({ ...junction, notes: e.target.value })}
                placeholder={t`Enter any additional info...`}
                className="resize-none min-h-28.5 rounded-[8px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} className="w-50 h-13 rounded-[10px]">{t`Cancel`}</Button>
            <Button onClick={handleLink} disabled={linkDisabled} className="w-50 h-13 rounded-[10px] gap-1.5">
              {isLinking && <Loader2 className="size-3.5 animate-spin" />}
              {t`Add container`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t`Edit Container`}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            {editingRow && (
              <div className="rounded-lg border border-(--color-border) bg-(--color-surface-muted)/30 px-3 py-2">
                <p className="text-sm font-medium">{editingRow.storageContainer.name}</p>
                {(editingRow.storageContainer.subType || editingRow.storageContainer.sizeOpen) && (
                  <p className="text-xs text-(--color-text-secondary)">{[editingRow.storageContainer.subType, editingRow.storageContainer.sizeOpen].filter(Boolean).join(" · ")}</p>
                )}
              </div>
            )}
            <JunctionFormFields form={editJunction} onChange={setEditJunction} codes={codes} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t`Cancel`}</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending} className="gap-1.5">
              {updateMutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t`Save`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Tools sub-tab ─────────────────────────────────────────────────────────────

function ToolsPanel({ recipeId, canManage }: { recipeId: number; canManage: boolean }) {
  const { t } = useLingui();
  const { data, isLoading, isError } = useRecipeTools(recipeId);
  const { data: flags } = useContainerFlags();
  const codes = flags?.codes;
  const toolTypeNames = useToolTypeNames();
  const tenantListsQuery = useTenantLists();
  const toolTypes = tenantListsQuery.data?.lists.toolTypes ?? TOOL_TYPES;
  const updateTenantListMutation = useUpdateTenantList();
  const createCatalogMutation = useCreateToolCatalog();
  const linkMutation = useLinkTool();
  const updateMutation = useUpdateRecipeTool();
  const unlinkMutation = useUnlinkTool();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Link dialog
  const [linkOpen, setLinkOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [debouncedCatalogSearch] = useDebounce(catalogSearch, 300);
  const [selectedCatalog, setSelectedCatalog] = useState<Tool | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newToolType, setNewToolType] = useState("");
  const [newToolTypeEntry, setNewToolTypeEntry] = useState("");
  const [newSizeSpec, setNewSizeSpec] = useState("");
  const [junction, setJunction] = useState<JunctionFields>(EMPTY_JUNCTION);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<RecipeTool | null>(null);
  const [editJunction, setEditJunction] = useState<JunctionFields>(EMPTY_JUNCTION);

  const allRows = data?.data ?? [];
  const filteredRows = search
    ? allRows.filter((r) =>
        r.tool.name.toLowerCase().includes(search.toLowerCase()) ||
        r.tool.toolType?.toLowerCase().includes(search.toLowerCase()),
      )
    : allRows;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const pagedRows = filteredRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  function openLink() {
    setSelectedCatalog(null);
    setCatalogSearch("");
    setCreateMode(false);
    setNewName("");
    setNewToolType("");
    setNewToolTypeEntry("");
    setNewSizeSpec("");
    setJunction(EMPTY_JUNCTION);
    setLinkOpen(true);
  }

  function addToolTypeEntry() {
    const candidate = newToolTypeEntry.trim();
    if (!candidate) {
      return;
    }
    const existing = toolTypes.find((value) => value.toLowerCase() === candidate.toLowerCase());
    setNewToolType(existing ?? candidate);
    setNewToolTypeEntry("");
  }

  function openEdit(row: RecipeTool) {
    setEditingRow(row);
    setEditJunction({ qty: row.qty, required: row.required, flag: row.flag ?? "", notes: row.notes ?? "" });
    setEditOpen(true);
  }

  async function handleLink() {
    try {
      let toolId: number;
      let createdToolType = "";
      if (createMode) {
        createdToolType = newToolType.trim();
        const created = await createCatalogMutation.mutateAsync({
          name: newName,
          toolType: createdToolType || undefined,
          sizeSpec: newSizeSpec || undefined,
        });
        toolId = created.id;
      } else {
        if (!selectedCatalog) return;
        toolId = selectedCatalog.id;
      }
      await linkMutation.mutateAsync({
        recipeId,
        data: {
          toolId,
          qty: junction.qty,
          required: junction.required,
          flag: junction.flag || undefined,
          notes: junction.notes || undefined,
        },
      });

      // Keep the tenant tool-type list in sync when a new custom type is used.
      if (
        createdToolType &&
        !toolTypes.some((value) => value.toLowerCase() === createdToolType.toLowerCase())
      ) {
        try {
          const latestLists = tenantListsQuery.data ?? (await tenantListsQuery.refetch()).data;
          // Never write without the server's list in hand: an empty fallback
          // would replace every type the tenant has with this one.
          if (!latestLists) throw new Error("Tool types are not loaded");
          const currentToolTypes = latestLists.lists.toolTypes ?? [];
          await updateTenantListMutation.mutateAsync({
            kind: "toolTypes",
            values: [
              ...currentToolTypes,
              ...(currentToolTypes.some((value) => value.toLowerCase() === createdToolType.toLowerCase())
                ? []
                : [createdToolType]),
            ],
          });
        } catch {
          // Tool linking already succeeded; a failed list update should not undo it.
        }
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save tool`));
      return;
    }
    toast.success(t`Tool added`);
    setLinkOpen(false);
  }

  async function handleUpdate() {
    if (!editingRow) return;
    try {
      await updateMutation.mutateAsync({
        recipeId,
        id: editingRow.id,
        data: {
          qty: editJunction.qty,
          required: editJunction.required,
          flag: editJunction.flag || undefined,
          notes: editJunction.notes || undefined,
        },
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save tool`));
      return;
    }
    toast.success(t`Tool updated`);
    setEditOpen(false);
  }

  const isLinking = createCatalogMutation.isPending || linkMutation.isPending;
  const linkDisabled = createMode ? !newName.trim() || isLinking : !selectedCatalog || isLinking;

  return (
    <>
      <TablePanel
        search={search} onSearch={(v) => { setSearch(v); setPage(1); }}
        canManage={canManage} onAdd={openLink} addLabel={t`Add tool`}
        isLoading={isLoading} isError={isError}
        headers={[t`Item Name`, t`Tool type`, t`Size/Spec`, t`Qty`, t`Required`, t`Flag`, t`Notes`]}
        rows={pagedRows} canManageRow={canManage}
        renderRow={(row) => (
          <TableRow key={row.id} className="h-17">
            <TableCell className="font-semibold text-[14px]">{row.tool.name}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.tool.toolType ?? <span className="text-(--color-text-secondary)">—</span>}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.tool.sizeSpec ?? <span className="text-(--color-text-secondary)">—</span>}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.qty}</TableCell>
            <TableCell className="text-[14px] font-medium">{row.required ? t`Yes` : <span className="text-(--color-text-secondary)">{t`Optional`}</span>}</TableCell>
            <TableCell><FlagDot flag={row.flag} codes={codes} /></TableCell>
            <TableCell className="text-[14px] font-medium text-(--color-text-secondary)">{row.notes ?? "—"}</TableCell>
            {canManage && (
              <TableCell>
                <RowMenu
                  onEdit={() => openEdit(row)}
                  onDelete={() =>
                    unlinkMutation.mutate(
                      { recipeId, id: row.id },
                      {
                        onSuccess: () => toast.success(t`Tool removed`),
                        onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to remove tool`)),
                      },
                    )
                  }
                  isDeleting={unlinkMutation.isPending}
                />
              </TableCell>
            )}
          </TableRow>
        )}
        emptyLabel={search ? t`No results match your search.` : t`No tools yet.`}
        page={page} totalPages={totalPages} onPageChange={setPage}
        total={filteredRows.length}
      />

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{createMode ? t`Create & Link Tool` : t`Add Tool`}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            {createMode ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setCreateMode(false)}
                  className="h-auto w-fit gap-1 p-0 text-sm text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
                >
                  <ChevronLeft className="size-4" />{t`Back to search`}
                </Button>
                <div className="flex flex-col gap-1.5">
                  <Label>{t`Name`} <span className="text-(--color-danger-text)">*</span></Label>
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t`e.g. Stock Pot, Chef Knife`} autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t`Tool type`}</Label>
                    <Select
                      value={newToolType || "none"}
                      onValueChange={(v) => setNewToolType(v === "none" ? "" : v)}
                    >
                      <SelectTrigger aria-label={t`Tool type`}>
                        <SelectValue placeholder={t`Select`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t`Unspecified`}</SelectItem>
                        {toolTypeOptions(newToolType, toolTypes).map((toolType) => (
                          <SelectItem key={toolType} value={toolType}>
                            {toolTypeNames[toolType] ?? toolType}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={newToolTypeEntry}
                        onChange={(e) => setNewToolTypeEntry(e.target.value)}
                        placeholder={t`Add new type`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addToolTypeEntry();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addToolTypeEntry}
                        disabled={newToolTypeEntry.trim() === ""}
                      >
                        {t`Add`}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t`Size / Spec`}</Label>
                    <Input value={newSizeSpec} onChange={(e) => setNewSizeSpec(e.target.value)} placeholder={t`e.g. 8qt, 10-inch`} />
                  </div>
                </div>
                <JunctionFormFields form={junction} onChange={setJunction} codes={codes} />
              </>
            ) : selectedCatalog ? (
              <>
                <div className="flex items-center justify-between rounded-lg border border-(--color-border) bg-(--color-surface-muted)/30 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{selectedCatalog.name}</p>
                    {(selectedCatalog.toolType || selectedCatalog.sizeSpec) && (
                      <p className="text-xs text-(--color-text-secondary)">{[selectedCatalog.toolType, selectedCatalog.sizeSpec].filter(Boolean).join(" · ")}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCatalog(null)}>{t`Change`}</Button>
                </div>
                <JunctionFormFields form={junction} onChange={setJunction} codes={codes} />
              </>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
                  <Input
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    placeholder={t`Search tools...`}
                    className="pl-9"
                    autoFocus
                  />
                </div>
                <div className="max-h-52 overflow-y-auto rounded-lg border border-(--color-border) divide-y divide-border">
                  <VirtualScroller<Tool>
                    queryKey={["tools", "catalog-picker", debouncedCatalogSearch]}
                    queryFn={(page, pageSize) =>
                      recipeRequests.listToolsCatalog({ page, limit: pageSize, search: debouncedCatalogSearch || undefined })
                    }
                    pageSize={20}
                    emptyMessage={t`No tools found.`}
                    renderSkeleton={() => <div className="h-10 bg-(--color-surface-muted)/30 animate-pulse" />}
                    loadingRows={3}
                    renderItem={(item) => (
                      <Button
                        key={item.id}
                        variant="ghost"
                        onClick={() => setSelectedCatalog(item)}
                        className="h-auto w-full flex-col items-start justify-start px-3 py-2.5 hover:bg-(--color-surface-muted)/50"
                      >
                        <span className="text-sm font-medium">{item.name}</span>
                        {(item.toolType || item.sizeSpec) && (
                          <span className="text-xs text-(--color-text-secondary)">{[item.toolType, item.sizeSpec].filter(Boolean).join(" · ")}</span>
                        )}
                      </Button>
                    )}
                  />
                  <Button
                    variant="ghost"
                    onClick={() => { setCreateMode(true); setNewName(catalogSearch); }}
                    className="h-auto w-full justify-start gap-2 px-3 py-2.5 text-sm font-medium text-(--color-action) hover:bg-(--color-surface-muted)/50 hover:text-(--color-action)"
                  >
                    <Plus className="size-4" />
                    {catalogSearch ? t`Create "${catalogSearch}"` : t`Create new tool`}
                  </Button>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>{t`Cancel`}</Button>
            <Button onClick={handleLink} disabled={linkDisabled} className="gap-1.5">
              {isLinking && <Loader2 className="size-3.5 animate-spin" />}
              {createMode ? t`Create & Link` : t`Link Tool`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t`Edit Tool`}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            {editingRow && (
              <div className="rounded-lg border border-(--color-border) bg-(--color-surface-muted)/30 px-3 py-2">
                <p className="text-sm font-medium">{editingRow.tool.name}</p>
                {(editingRow.tool.toolType || editingRow.tool.sizeSpec) && (
                  <p className="text-xs text-(--color-text-secondary)">{[editingRow.tool.toolType, editingRow.tool.sizeSpec].filter(Boolean).join(" · ")}</p>
                )}
              </div>
            )}
            <JunctionFormFields form={editJunction} onChange={setEditJunction} codes={codes} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t`Cancel`}</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending} className="gap-1.5">
              {updateMutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t`Save`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Shared table shell ────────────────────────────────────────────────────────

interface TablePanelProps<T> {
  search: string;
  onSearch: (v: string) => void;
  canManage: boolean;
  onAdd: () => void;
  addLabel: string;
  isLoading: boolean;
  isError: boolean;
  headers: string[];
  rows: T[];
  canManageRow: boolean;
  renderRow: (row: T) => React.ReactNode;
  emptyLabel: string;
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  total: number;
}

function TablePanel<T extends { id: string | number }>({
  search, onSearch, canManage, onAdd, addLabel,
  isLoading, isError, headers, rows, canManageRow,
  renderRow, emptyLabel, page, totalPages, onPageChange, total,
}: TablePanelProps<T>) {
  const { t } = useLingui();
  const [view, setView] = useState<"table" | "list">("table");

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        <div className="relative w-75">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-(--color-text-secondary)" />
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={t`Search...`} className="pl-11 h-11 rounded-lg border-(--color-border) shadow-xs" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium"><Columns2 className="size-5" />{t`Columns`}</Button>
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium"><ArrowUpDown className="size-5" />{t`Sort`}</Button>
          <Button variant="outline" size="sm" className="h-11 gap-2 rounded-[10px] font-medium"><SlidersHorizontal className="size-5" />{t`Filter`}</Button>
          <div className="flex h-11 items-center gap-0 rounded-lg bg-(--color-surface-muted) p-1" role="group" aria-label={t`View mode`}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setView("table")}
              aria-label={t`Table view`}
              aria-pressed={view === "table"}
              className={`size-9 [&_svg]:size-5 ${view === "table" ? "bg-(--color-surface-page) shadow-xs hover:bg-(--color-surface-page)" : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-transparent"}`}
            >
              <Table2 />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setView("list")}
              aria-label={t`List view`}
              aria-pressed={view === "list"}
              className={`size-9 [&_svg]:size-5 ${view === "list" ? "bg-(--color-surface-page) shadow-xs hover:bg-(--color-surface-page)" : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-transparent"}`}
            >
              <List />
            </Button>
          </div>
          {canManage && (
            <Button size="sm" onClick={onAdd} className="h-11 gap-2 rounded-[10px]">
              <Plus className="size-4" />{addLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-[10px] border border-(--color-border) overflow-hidden">
        {isLoading ? (
          <div className="space-y-0">
            <div className="h-13 bg-(--color-surface-muted)/50 border-b border-(--color-border)" />
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-(--color-border) last:border-0">
                <Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-10" /><Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-6 rounded-full" /><Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-sm text-(--color-danger-text)">{t`Failed to load data. Please try again.`}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-(--color-surface-muted)/50 hover:bg-(--color-surface-muted)/50 h-13">
                <TableHead className="text-[13px]">
                  <Button variant="ghost" className="h-auto gap-1 p-0 font-medium text-(--color-text-secondary) hover:bg-transparent">{headers[0]}<ChevronDown className="size-4" /></Button>
                </TableHead>
                <TableHead className="w-35 text-[13px] font-medium text-(--color-text-secondary)">{headers[1]}</TableHead>
                <TableHead className="w-35 text-[13px] font-medium text-(--color-text-secondary)">{headers[2]}</TableHead>
                <TableHead className="w-20 text-[13px] font-medium text-(--color-text-secondary)">{headers[3]}</TableHead>
                <TableHead className="w-25 text-[13px] font-medium text-(--color-text-secondary)">{headers[4]}</TableHead>
                <TableHead className="w-35 text-[13px] font-medium text-(--color-text-secondary)">{headers[5]}</TableHead>
                <TableHead className="w-50 text-[13px] font-medium text-(--color-text-secondary)">{headers[6]}</TableHead>
                {canManageRow && <TableHead className="w-11" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageRow ? 8 : 7} className="h-40 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-sm text-(--color-text-secondary)">{emptyLabel}</p>
                      {!search && canManage && (
                        <Button variant="outline" size="sm" onClick={onAdd} className="gap-1.5"><Plus className="size-4" />{addLabel}</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => renderRow(row))
              )}
            </TableBody>
          </Table>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-3 border-t border-(--color-border)">
          <p className="text-sm text-(--color-text-secondary)">{t`0 of ${total} row(s) selected.`}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-(--color-text-secondary)">{t`Rows per page`}</span>
              <div className="flex h-11 w-24 items-center justify-between rounded-lg border border-(--color-border) bg-(--color-surface-page) px-4 py-3 shadow-xs text-[16px]">
                <span>{ROWS_PER_PAGE}</span><ChevronsUpDown className="size-4 text-(--color-text-secondary)" />
              </div>
            </div>
            <span className="text-sm font-medium">{t`Page ${page} of ${totalPages}`}</span>
            <div className="flex items-center gap-2">
              {[
                { icon: ChevronsLeft, action: () => onPageChange(1), disabled: page === 1 },
                { icon: ChevronLeft, action: () => onPageChange(Math.max(1, page - 1)), disabled: page === 1 },
                { icon: ChevronRight, action: () => onPageChange(Math.min(totalPages, page + 1)), disabled: page === totalPages },
                { icon: ChevronsRight, action: () => onPageChange(totalPages), disabled: page === totalPages },
              ].map(({ icon: Icon, action, disabled }, i) => (
                <Button key={i} variant="outline" size="icon" onClick={action} disabled={disabled}
                  className="size-11 rounded-lg shadow-xs">
                  <Icon className="size-5" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Row action menu ──────────────────────────────────────────────────────────

function RowMenu({ onEdit, onDelete, isDeleting }: { onEdit: () => void; onDelete: () => void; isDeleting: boolean }) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-11 rounded-[10px]"><MoreHorizontal className="size-5" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit} className="gap-2"><Pencil className="size-4" />{t`Edit`}</DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete} className="gap-2 text-(--color-danger-text) focus:text-(--color-danger-text)" disabled={isDeleting}>
          <Trash2 className="size-4" />{t`Delete`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Root component ────────────────────────────────────────────────────────────

export function StorageContainersTab({ recipe, canManage = false }: StorageContainersTabProps) {
  const { t } = useLingui();
  const recipeId = recipe.id;
  const [subTab, setSubTab] = useState<SubTab>("containers");

  return (
    <div className="flex flex-col gap-3 px-6">
      {/* Heading */}
      <div className="flex items-center justify-between pl-2">
        <h2 className="text-2xl font-medium text-(--color-text-primary)">{t`Storage Containers & Tools`}</h2>
        <div className="flex h-10 w-60 items-center rounded-lg bg-(--color-surface-muted)/50 border border-(--color-border) p-1">
          {(["containers", "tools"] as SubTab[]).map((tab) => (
            <Button
              key={tab}
              variant="ghost"
              onClick={() => setSubTab(tab)}
              className={`h-full flex-1 text-sm font-medium ${
                subTab === tab ? "bg-(--color-surface-page) shadow-xs text-(--color-text-primary) hover:bg-(--color-surface-page)" : "text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
              }`}
            >
              {tab === "containers" ? t`Containers` : t`Tools`}
            </Button>
          ))}
        </div>
      </div>

      {subTab === "containers"
        ? <ContainersPanel recipeId={recipeId} canManage={canManage} />
        : <ToolsPanel recipeId={recipeId} canManage={canManage} />
      }
    </div>
  );
}
